/**
 * AdminFoundationsRaw — a small admin-only preview harness for
 * newly-uploaded raw Foundations voice takes.
 *
 * Michael, Feb 2026:
 *   "I want to hear the takes back before they're wired into
 *    Foundations. Just three players, nothing else."
 *
 * Each row streams the raw take from
 *   GET /api/foundations/admin/preview-raw/{slot}
 * (admin-gated) using an authenticated fetch → object URL so the
 * <audio> element can play it without leaking the bearer token in a
 * plain query string.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token") || ""}` },
});

const SLOTS = [
    { id: "rest", label: "Rest", note: "Approved script · warm, unhurried" },
    { id: "self_belief", label: "Self-Belief", note: "Approved script · kinder inner voice" },
    { id: "moment_for_me", label: "A Moment for Me", note: "Mini guided meditation" },
    { id: "you_are_not_alone", label: "You Are Not Alone", note: "Bonus · companion energy" },
    { id: "kindness_for_body", label: "A Little Kindness for Your Body", note: "Bonus · 6:21 gratitude arc" },
    { id: "difficult_day", label: "For a Difficult Day", note: "Bonus · press-play grounding" },
];

function RawPlayer({ slot }) {
  const [status, setStatus] = useState("loading"); // loading | ready | missing | error
  const [blobUrl, setBlobUrl] = useState(null);
  const [meta, setMeta] = useState({ size: 0 });

  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/foundations/admin/preview-raw/${slot.id}`,
          { ...auth(), responseType: "blob" },
        );
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
        setMeta({ size: r.data.size });
        setStatus("ready");
      } catch (e) {
        if (e.response?.status === 404) setStatus("missing");
        else setStatus("error");
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [slot.id]);

  return (
    <div
      data-testid={`admin-foundations-raw-${slot.id}`}
      className="rounded-2xl p-5 mb-4"
      style={{
        background: "rgba(245,236,217,0.04)",
        border: "1px solid rgba(224,169,122,0.3)",
      }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div>
          <div style={{ fontFamily: '"Playfair Display", serif', fontSize: "1.35rem", color: "#f5ecd9" }}>
            {slot.label}
          </div>
          <div style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.75rem", color: "rgba(245,236,217,0.55)", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            {slot.note}
          </div>
        </div>
        <div style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.7rem", color: "rgba(224,169,122,0.7)" }}>
          {status === "ready" ? `${Math.round(meta.size / 1024)} KB` : status}
        </div>
      </div>
      {status === "ready" && blobUrl && (
        <audio controls preload="metadata" style={{ width: "100%" }} data-testid={`admin-foundations-raw-audio-${slot.id}`}>
          <source src={blobUrl} type="audio/mpeg" />
        </audio>
      )}
      {status === "missing" && (
        <div style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.8rem", color: "rgba(245,236,217,0.55)" }}>
          No raw take uploaded yet for this slot.
        </div>
      )}
      {status === "error" && (
        <div style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.8rem", color: "#e07a7a" }}>
          Could not load — check admin permissions.
        </div>
      )}
    </div>
  );
}

export default function AdminFoundationsRaw() {
  const slots = useMemo(() => SLOTS, []);
  return (
    <div
      className="min-h-screen p-8"
      style={{ background: "#0a0f1a", color: "#f5ecd9" }}
      data-testid="admin-foundations-raw-page"
    >
      <div className="max-w-2xl mx-auto">
        <h1
          className="italic"
          style={{ fontFamily: '"Playfair Display", serif', fontSize: "2rem", fontWeight: 300 }}
        >
          Foundations · Raw Takes
        </h1>
        <p
          className="mt-2 mb-8 italic"
          style={{ fontFamily: '"Playfair Display", serif', color: "rgba(245,236,217,0.7)" }}
        >
          Review the freshly-uploaded voice takes before they're wired into the Foundations catalogue.
        </p>
        {slots.map((s) => <RawPlayer key={s.id} slot={s} />)}
      </div>
    </div>
  );
}
